// Picking files from Google Drive, for an import: Google's own sign-in hands over a token scoped to
// "drive.file", then Google's picker lets the person choose files, and both come back for the API,
// which does the copying. The token only ever reaches the files picked with it.
//
// Neither piece is on npm, so their scripts are loaded from Google the first time they're needed.
// Google's sign-in opens a popup, which a browser only allows straight after a click, so the
// scripts are best loaded ahead of it: "prepareGooglePicker" when import is offered, and
// "pickFromGoogle" on the click itself, which then has nothing left to wait for.
const IDENTITY_SCRIPT = 'https://accounts.google.com/gsi/client'
const PICKER_SCRIPT = 'https://apis.google.com/js/api.js'
const SCOPE = 'https://www.googleapis.com/auth/drive.file'

const loadScript = src => new Promise((resolve, reject) => {
  const script = document.createElement('script')

  script.src = src
  script.async = true
  script.onload = resolve
  script.onerror = () => reject(new Error(`Google’s script at ${src} couldn’t be loaded`))
  document.head.appendChild(script)
})

const loadPicker = () => loadScript(PICKER_SCRIPT).then(() => new Promise(resolve => window.gapi.load('picker', resolve)))

const isLoaded = () => Boolean(window.google?.accounts?.oauth2 && window.google?.picker)

let preparing = null

/** Loads Google's sign-in and picker once, and again only after a load that failed. */
export const prepareGooglePicker = () => {
  if (isLoaded()) return Promise.resolve()

  preparing ??= Promise.all([loadScript(IDENTITY_SCRIPT), loadPicker()]).catch(error => {
    preparing = null

    throw error
  })

  return preparing
}

// Asks again without a prompt for someone who has already agreed, so only the first import shows
// Google's consent screen.
const requestToken = () => new Promise((resolve, reject) => {
  const client = window.google.accounts.oauth2.initTokenClient({
    client_id: GOOGLE_CLIENT_ID,
    scope: SCOPE,
    callback: response => (response.error ? reject(new Error(response.error)) : resolve(response.access_token)),
    error_callback: error => reject(new Error(error.type))
  })

  client.requestAccessToken({prompt: ''})
})

// Files only: a folder picked with "drive.file" doesn't open the files inside it to the token.
const choose = accessToken => new Promise(resolve => {
  const {picker} = window.google
  const view = new picker.DocsView(picker.ViewId.DOCS).setIncludeFolders(true).setSelectFolderEnabled(false)

  new picker.PickerBuilder()
    .addView(view)
    .enableFeature(picker.Feature.MULTISELECT_ENABLED)
    .setOAuthToken(accessToken)
    .setDeveloperKey(GOOGLE_API_KEY)
    .setAppId(GOOGLE_APP_ID)
    .setCallback(data => {
      if (data.action === picker.Action.PICKED) resolve(data.docs.map(({id}) => id))
      if (data.action === picker.Action.CANCEL) resolve([])
    })
    .build()
    .setVisible(true)
})

/**
 * The token and the ids of the files picked, none when the picker was closed. Rejects when Google
 * couldn't be loaded or the person didn't let the drive in.
 */
export const pickFromGoogle = async () => {
  await prepareGooglePicker()

  const accessToken = await requestToken()
  const fileIds = await choose(accessToken)

  return {accessToken, fileIds}
}
